import { startOfDay } from 'date-fns';
import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { DatePicker } from '@components/datepicker';
import { DateFieldType, DateRestrictionType, formatDateWithoutTimezone } from '@components/form/v2';
import { HorizontalTabItem } from '@components/horizontal-tabs';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconDate } from '@components/icons/date';
import { IconSettings } from '@components/icons/settings';
import { Select, SelectOption } from '@components/select';
import { Stack } from '@components/stack';
import { LabeledSwitch } from '@components/switch';
import { Text } from '@components/text';
import { getManualDefaultValue } from '@components/text-editors/addons/field-mapping';
import { TextField } from '@components/text-field';
import { getScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { parseDate } from '@utils/date';
import { asteriskLabel } from '@utils/strings';

import { FieldDropzone } from './field-dropzone';

import { conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import {
  clearManualDateDefaultIfInvalid,
  DefaultValueMapping,
  DefaultValueMappingDisplay,
} from '../default-value-mapping';
import { DescriptionInput } from '../description-input';
import { FieldEdit } from '../field-edit';
import { FieldConfig } from '../field-registry';
import { FieldSidebarLabel } from '../fields-sidebar';
import { RequiredSwitch } from '../required-switch';

type Config = FieldConfig<'input-date'>;

const Display: Config['Display'] = ({ field }) => {
  const manualValue = getManualDefaultValue(field.mappedDefaultValue);
  return (
    <DefaultValueMappingDisplay field={field}>
      <DatePicker
        key={manualValue ?? 'empty'}
        label={asteriskLabel(field.label, field.isRequired)}
        description={field.description}
        placeholder={field.placeholder}
        labelIcon={field.companyAttributeId ? <IconAttributeTraveller size={16} /> : undefined}
        defaultValue={manualValue || undefined}
      />
    </DefaultValueMappingDisplay>
  );
};

export const FormBuilderDate: Config['Component'] = ({ field, dragHandle }) => {
  const actions = useFormBuilderContext().store.actions;

  return (
    <FieldDropzone field={field}>
      <FieldEdit
        field={field}
        dragHandle={dragHandle}
        display={<Display field={field} />}
        edit={
          <>
            <TextField
              onChange={actions.createChangeHandler(field, 'label')}
              defaultValue={field.label}
              label={t('label')}
              maxChars={255}
            />
            <DescriptionInput field={field} />
            <TextField
              onChange={actions.createChangeHandler(field, 'placeholder')}
              defaultValue={field.placeholder}
              label={t('statePlaceholder')}
            />
            <RequiredSwitch field={field} />
            <DefaultValueMapping field={field} />
          </>
        }
      />
    </FieldDropzone>
  );
};

const DatePropertiesTab = ({ field }: { field: DateFieldType }) => {
  const {
    store: { actions },
  } = useFormBuilderContext();

  const dateRestrictionOptions: SelectOption[] = [
    { label: t('option_disablePast'), value: 'disablePast' },
    { label: t('option_disableFuture'), value: 'disableFuture' },
    { label: t('option_range'), value: 'range' },
  ];

  const hasRestriction = field.dateRestrictionType && field.dateRestrictionType !== 'none';

  const setMappedDefault = actions.createChangeHandler(field, 'mappedDefaultValue');

  const buildRestrictions = (
    restrictionType: DateRestrictionType,
    rangeStart: string | undefined,
    rangeEnd: string | undefined
  ) => ({
    disablePastDates: restrictionType === 'disablePast',
    disableFutureDates: restrictionType === 'disableFuture',
    minDate: restrictionType === 'range' && rangeStart ? parseDate(rangeStart) : undefined,
    maxDate: restrictionType === 'range' && rangeEnd ? parseDate(rangeEnd) : undefined,
  });

  const handleRestrictionToggle = (enabled: boolean) => {
    if (enabled) {
      actions.createChangeHandler(field, 'dateRestrictionType')('disablePast');
      clearManualDateDefaultIfInvalid(
        field.mappedDefaultValue,
        buildRestrictions('disablePast', field.dateRangeStart, field.dateRangeEnd),
        setMappedDefault
      );
    } else {
      actions.createChangeHandler(field, 'dateRestrictionType')('none');
      actions.createChangeHandler(field, 'dateRangeStart')(undefined);
      actions.createChangeHandler(field, 'dateRangeEnd')(undefined);
    }
  };

  const handleRestrictionTypeChange = (value: string | null) => {
    if (!value) return;
    const restrictionType = value as DateRestrictionType;
    actions.createChangeHandler(field, 'dateRestrictionType')(restrictionType);

    const nextRangeStart = restrictionType === 'range' ? field.dateRangeStart : undefined;
    const nextRangeEnd = restrictionType === 'range' ? field.dateRangeEnd : undefined;
    if (restrictionType !== 'range') {
      actions.createChangeHandler(field, 'dateRangeStart')(undefined);
      actions.createChangeHandler(field, 'dateRangeEnd')(undefined);
    }
    clearManualDateDefaultIfInvalid(
      field.mappedDefaultValue,
      buildRestrictions(restrictionType, nextRangeStart, nextRangeEnd),
      setMappedDefault
    );
  };

  const handleStartDateChange = (date: Date) => {
    const iso = formatDateWithoutTimezone(date);
    actions.createChangeHandler(field, 'dateRangeStart')(iso);
    clearManualDateDefaultIfInvalid(
      field.mappedDefaultValue,
      buildRestrictions(field.dateRestrictionType, iso, field.dateRangeEnd),
      setMappedDefault
    );
  };

  const handleEndDateChange = (date: Date) => {
    const iso = formatDateWithoutTimezone(date);
    actions.createChangeHandler(field, 'dateRangeEnd')(iso);
    clearManualDateDefaultIfInvalid(
      field.mappedDefaultValue,
      buildRestrictions(field.dateRestrictionType, field.dateRangeStart, iso),
      setMappedDefault
    );
  };

  return (
    <Content>
      <LabeledSwitch
        label={t('restrictDateSelection')}
        value={hasRestriction}
        onChange={handleRestrictionToggle}
      />
      <Collapse isCollapsed={!hasRestriction}>
        <Stack space={4}>
          <Stack space={2}>
            <Select
              label={t('restrictionType')}
              hideLabel
              value={field.dateRestrictionType}
              options={dateRestrictionOptions}
              onChange={handleRestrictionTypeChange}
              isClearable={false}
              isSearchable={false}
            />
            <Text
              font="regular14"
              color="textHelper"
              text={t(`help_${field.dateRestrictionType}`)}
            />
          </Stack>

          {field.dateRestrictionType === 'range' && (
            <Stack space={3}>
              <DatePicker
                label={t('startDate')}
                value={field.dateRangeStart ? parseDate(field.dateRangeStart) : undefined}
                onChange={handleStartDateChange}
                placeholder={t('selectDate')}
                maxDate={field.dateRangeEnd ? parseDate(field.dateRangeEnd) : undefined}
              />
              <DatePicker
                label={t('endDate')}
                value={field.dateRangeEnd ? parseDate(field.dateRangeEnd) : undefined}
                onChange={handleEndDateChange}
                placeholder={t('selectDate')}
                minDate={field.dateRangeStart ? parseDate(field.dateRangeStart) : undefined}
              />
            </Stack>
          )}
        </Stack>
      </Collapse>
    </Content>
  );
};

const propertiesTab = (field: DateFieldType): HorizontalTabItem => {
  return {
    label: t('properties'),
    icon: IconSettings,
    content: <DatePropertiesTab field={field} />,
  };
};

const { t } = getScopedTranslate({
  en: {
    title: 'Date',
    label: 'Label',
    statePlaceholder: 'Placeholder text',
    properties: 'Properties',
    restrictDateSelection: 'Restrict date selection',
    restrictionType: 'Restriction type',
    option_disablePast: 'Disable past dates',
    option_disableFuture: 'Disable future dates',
    option_range: 'Restrict date range',
    startDate: 'Start date',
    endDate: 'End date',
    selectDate: 'Select date',
    help_none: '',
    help_disablePast: 'Only dates in the future can be selected',
    help_disableFuture: 'Only dates in the past can be selected',
    help_range: 'Only dates between these values can be selected',
  },
});

export const dateConfig: Config = {
  kind: 'input-date',
  title: t('title'),
  group: 'inputs',
  Icon: IconDate,
  TreeDisplay: ({ field }) => <FieldSidebarLabel label={field.label} />,
  Component: FormBuilderDate,
  Display: Display,
  propertiesTabs: [propertiesTab, conditionsTab],
  condition: {
    comparators: ['before', 'on', 'after'],
    ValueMenu: ({ value, onSelect }) => (
      <DatePicker
        value={value ? new Date(value as string) : ''}
        onChange={(val) => onSelect(startOfDay(val).toISOString())}
        inline
      />
    ),
  },
};

const Content = styled(Stack).attrs({ space: 4 })({
  padding: pxToRem(24),
});
